import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { wsClient, WS_EVENTS } from '../api/websocket';
import { useSession } from './SessionContext';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { sessionId, isConnected: sessionConnected } = useSession();
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const listenersRef = useRef({
    chunk: new Set(),
    complete: new Set(),
    error: new Set()
  });

  // Connect/disconnect WebSocket when session changes. `connected` follows the
  // socket's open and close events below, so a reconnect restores it too.
  useEffect(() => {
    if (!sessionId || !sessionConnected) return undefined;

    wsClient.connect(sessionId).catch((err) => setError(err.message));

    return () => {
      wsClient.disconnect();
      setConnected(false);
    };
  }, [sessionId, sessionConnected]);

  // Set up WebSocket event listeners
  useEffect(() => {
    const unsubChunk = wsClient.on(WS_EVENTS.CHAT_CHUNK, (payload) => {
      listenersRef.current.chunk.forEach(cb => cb(payload.text));
    });

    const unsubComplete = wsClient.on(WS_EVENTS.CHAT_COMPLETE, (payload) => {
      listenersRef.current.complete.forEach(cb => cb(payload));
    });

    const unsubError = wsClient.on(WS_EVENTS.CHAT_ERROR, (payload) => {
      listenersRef.current.error.forEach(cb => cb(payload));
    });

    const unsubOpen = wsClient.on('open', () => {
      setConnected(true);
      setError(null);
    });

    const unsubClose = wsClient.on('close', () => {
      setConnected(false);
    });

    const unsubReconnectFailed = wsClient.on('reconnect_failed', () => {
      setError('Connection lost. Please refresh the page.');
    });

    return () => {
      unsubChunk();
      unsubComplete();
      unsubError();
      unsubOpen();
      unsubClose();
      unsubReconnectFailed();
    };
  }, []);

  const sendMessage = useCallback((question, context = null, personaPrompt = null) => {
    wsClient.sendChatMessage(question, context, personaPrompt);
  }, []);

  const cancelStream = useCallback(() => {
    wsClient.cancelChat();
  }, []);

  const onChunk = useCallback((callback) => {
    listenersRef.current.chunk.add(callback);
    return () => listenersRef.current.chunk.delete(callback);
  }, []);

  const onComplete = useCallback((callback) => {
    listenersRef.current.complete.add(callback);
    return () => listenersRef.current.complete.delete(callback);
  }, []);

  const onError = useCallback((callback) => {
    listenersRef.current.error.add(callback);
    return () => listenersRef.current.error.delete(callback);
  }, []);

  const value = {
    connected,
    error,
    sendMessage,
    cancelStream,
    onChunk,
    onComplete,
    onError
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

export default WebSocketContext;
